import React, {RefObject, useEffect, useState} from 'react'
import {FieldErrorSmallLabel} from '../../../../styledComponents/DisplayItem'
import styled from 'styled-components'
import {Nullable} from '../../../../app/TypeHelpers'
import {MessagePair} from '../FormErrorMessage'

interface ErrorDisplayProps {
    errorAreaMaxWidth?: number
    messageInvisible?: boolean
    messages?: MessagePair[]
}

export interface ErrorProps {
    hasError: boolean
}

export const ResidentHocInputErrorHandling = <OriginalProps extends {}>(
    WrappedComponent: React.ComponentType<OriginalProps & ErrorProps>
) => {

    type ResultProps = OriginalProps & ErrorDisplayProps

    const formatEmbeddedParameters = (messagePair: MessagePair): string => {
        if (!messagePair.params) {
            return messagePair.message
        }

        let message = messagePair.message
        messagePair.params.forEach((param, i) => {
            message = message.replace(`{${i}}`, param)
        })
        return message
    }

    const createMessage = (pairs?: MessagePair[]): Nullable<string[]> => {
        if (!pairs) {
            return null
        }

        return pairs.map((messagePair) => {
            return formatEmbeddedParameters(messagePair)
        }).filter((value, index, self) => self.indexOf(value) === index)
    }

    const InputError: React.FC<ResultProps> = (props) => {
        const [areaWidth, setAreaWidth] = useState(0)
        useEffect(() => {
            handleResize()
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        })
        const {errorAreaMaxWidth, messageInvisible} = props
        const inputFieldRef: RefObject<HTMLDivElement> = React.createRef()

        const embeddedMessages = createMessage(props.messages)
        if (!embeddedMessages) {
            return null
        }

        const {messages, ...rest} = props

        const handleResize = (): void => setAreaWidth(() => {
            const inputField = inputFieldRef.current as HTMLDivElement | null;
            if (!inputField) {
                // console.warn('[handleResize]inputFieldRef.currentがnull')
                return 0;
            }
            const errorMessageAreaWrapper = inputField.closest('[data-error-area-scale="true"]')
            if (!errorMessageAreaWrapper) {
                // console.warn('[handleResize]errorMessageAreaWrapperが見つからない')
                return 0;
            }
            return errorMessageAreaWrapper.clientWidth;
        })


        return (
            <InputField ref={inputFieldRef}>
                <WrappedComponent
                    {...props}
                    {...rest}
                    hasError={!!(messages && messages.length > 0)}
                />

                {
                    !messageInvisible && embeddedMessages.map((message, i) => {
                        return <FormErrorLabel
                            key={i}
                            areaWidth={areaWidth}
                            errorAreaMaxWidth={errorAreaMaxWidth}>
                            {message}
                        </FormErrorLabel>
                    })
                }
            </InputField>
        )
    }
    InputError.displayName = 'InputError'
    return InputError
}


const InputField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const FormErrorLabel = styled(FieldErrorSmallLabel)<{ errorAreaMaxWidth?: number, areaWidth: number }>`
  ${props => props.areaWidth !== 0 && `
      width: ${props.areaWidth - 28}px;
  `};
  ${props => props.errorAreaMaxWidth && `
      max-width: ${props.errorAreaMaxWidth}px;
  `};
`
